import React, {useEffect} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import VteForm from './vte_form';
import AssignmentIcon from '@mui/icons-material/Assignment';
import moment from 'moment';
import {hasPermission, Permissions} from '../../../../store/reducers/user';
import {createNotesForVte, getOutcomeView} from './vteUtils';
import isEqual from 'lodash/isEqual';
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {getTableScrollbar} from '../../../../util/scrollbar';
import {getConfig} from '../../../../util/configUtil';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    modalTitle: {
        textAlign: 'center',
        fontSize: 20,
        fontWeight: 'bold',
    },
    modalFooter: {
        height: 50,
        position: 'relative',
        bottom: 0,
    },
    modalHeader: {
        height: 50,
        position: 'relative',
        top: 0,
    },
    modalBody: {
        position: 'relative',
        height: 'calc(100% - 100px)',
        overflow: 'auto',
        display: 'flex',
    },
    header2: {
        fontWeight: 'bold',
        marginBottom: 10,
        fontSize: 16,
    },
    sectionItem: {
        marginTop: 10,
        borderRadius: '5px',
        display: 'inline-flex',
        width: 'calc(100% - 5px)',
        cursor: 'pointer',
        padding: '7px 10px',
        fontWeight: 'normal',
        lineHeight: '24px',
        fontSize: '10px',
        backgroundColor: theme.palette.background.paperDarker,
        border: `1px solid ${theme.palette.inPatient.iconBackground}`,
        '&:hover': {
            opacity: 0.7,
        },
    },
    sectionSelectedItem: {
        marginTop: 10,
        borderRadius: '5px',
        display: 'inline-flex',
        width: 'calc(100% - 5px)',
        cursor: 'pointer',
        padding: '7px 10px',
        fontWeight: 'normal',
        lineHeight: '24px',
        fontSize: '10px',
        backgroundColor: theme.palette.background.paperDarker,
        border: `1px solid ${theme.palette.primary.main}`,
    },
    divider: {
        margin: '5px 20px',
        border: `1px solid ${theme.palette.inPatient.inputBackground}`,
        height: 'auto',
    },
    vteList: {
        height: 'calc(100vh - 400px)',
        maxWidth: 350,
        minWidth: 250,
        paddingRight: 10,
        overflow: 'auto',
    },
    vteOutcome: {
        height: 'calc(100vh - 320px)',
        width: '100%',
        overflow: 'auto',
        paddingRight: 10,
    },
}));

const VIEWS = {
    MENU: 1,
    FORM: 2,
};

function InlineFormVTE(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [forms, setForms] = React.useState(props.patient?.vte || []);
    const [view, setView] = React.useState(forms.length ? VIEWS.MENU : VIEWS.FORM);
    const [formToEdit, setFormToEdit] = React.useState(null);
    const [formToView, setFormToView] = React.useState(null);
    const canEditVte = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_LOWER_LIMB_VTE));
    const [formTemp, setFormTemp] = React.useState(null);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    useEffect(() => {
        if (props.onSubmitFunction) {
            props.onSubmitFunction(saveForm);
        }
    }, [forms, formTemp]);

    useEffect(() => {
        const isView = view === VIEWS.MENU;
        if (props.onHideSubmit) props.onHideSubmit(isView);
        if (isView) {
            setFormToView(forms[forms.length - 1]);
        }
    }, [view]);

    useEffect(() => {
        const buttons = [];
        if (view === VIEWS.MENU) {
            buttons.push([
                <AgyleButton
                    disabled={!canEditVte || props.disableSave}
                    key="add"
                    startIcon={<ReplayIcon/>}
                    onClick={openFormToEdit}
                    colour="primary"
                >
                    {t('Revalidate')}
                </AgyleButton>,
                <AgyleButton
                    key="Add"
                    disabled={!canEditVte || props.disableSave}
                    startIcon={<AddIcon/>}
                    onClick={openNewForm}
                    colour="primary"
                >
                    {t('Add New Vte')}
                </AgyleButton>,
            ]);
        }
        if (props.onCustomButtons) props.onCustomButtons(buttons);
    }, [view, canEditVte, props.disableSave, formToView]);

    const getVteForms = () => {
        let formsHtml = [];
        forms?.forEach((form, index) => {
            const selected = formToView === form;
            formsHtml.push(
                <div
                    key={index + 'vteForms'}
                    onClick={() => setFormToView(form)}
                    className={selected ? classes.sectionSelectedItem : classes.sectionItem}
                >
                    <div>
                        <div>
                            <AssignmentIcon color={selected ? 'primary' : 'inherit'}/>
                            <b style={{marginTop: -32, display: 'block', marginLeft: 30}}>
                                {moment(form.createdWhen).format(defaultDateTimeFormat)}
                            </b>
                        </div>
                        <div> Created by: {form.createdWho} </div>
                    </div>
                </div>,
            );
        });
        return formsHtml.reverse();
    };

    const openFormToEdit = () => {
        setFormToEdit(formToView);
        setView(VIEWS.FORM);
    };

    const openNewForm = () => {
        setFormToEdit(null);
        setView(VIEWS.FORM);
    };

    const saveForm = () => {
        let newForms = [...forms];
        newForms.push(formTemp);
        setForms(newForms);
        setView(VIEWS.MENU);
        props.onChange([{
            name: 'vte',
            value: newForms,
            noteToAdd: createNotesForVte(formTemp),
        }]);
        setFormTemp(null);
    };

    const handleDisableSave = (val) => {
        if (props.onDisableSubmit)
            props.onDisableSubmit(val);
    };

    const handleSaveTempData = (val) => {
        if (!isEqual(val, formTemp)) setFormTemp(val);
    };

    return (
        <>
            {view === VIEWS.FORM &&
                <VteForm
                    formToEdit={formToEdit}
                    patient={props.patient}
                    onChange={handleSaveTempData}
                    disableSubmit={handleDisableSave}
                    disableSave={props.disableSave}
                />
            }
            {view === VIEWS.MENU &&
                <>
                    <div className={classes.modalBody}>
                        <div>
                            <div className={classes.header2}>
                                {t('History')}
                            </div>

                            <div className={clsx(getTableScrollbar(), classes.vteList)}>
                                {getVteForms()}
                            </div>
                        </div>

                        <div className={classes.divider}/>

                        {formToView &&
                            <div className={clsx(getTableScrollbar(), classes.vteOutcome)}>
                                {getOutcomeView(formToView, classes, t)}
                            </div>
                        }
                    </div>
                </>
            }
        </>
    );
}

export default InlineFormVTE;
